<template>
    <filter-panel
        :filterOptions="filterOptions"
        :collapsed="true"
        :model-value="filterState"
        @update:modelValue="onUpdateFilters"
    />
    <v-data-table
        :headers="requestProtocolHeaders"
        :items="filteredRequestProtocols"
        item-key="id"
        class="elevation-1"
        :items-per-page="25"
        :search="search"
    >
        <template v-slot:item.url="{ item }">
            <a v-if="item.url" :href="item.url" target="_blank">View Page</a>
            <span v-else>N/A</span>
        </template>
        <template v-slot:item.actions="{ item }">
            <v-row>
                <v-icon @click="viewRequestProtocol(item)">$eye</v-icon>
            </v-row>
        </template>
    </v-data-table>
</template>

<script setup lang="ts">
import { buildFilterOptionsFromData, filterData } from "@/lib/filterUtils";
import { RequestProcessor, SavedRequestProtocol } from "@/services/samples/samplesAPI";
import { ref, computed, watch } from "vue";
import FilterPanel from "../common/FilterPanel.vue";
import { ContainerType } from "@/services/commonModels";
import { useRouter } from "vue-router";

const router = useRouter();

const { requestProtocols, requestProcessors, containerTypes, search } = defineProps<{
    requestProtocols: SavedRequestProtocol[];
    requestProcessors: RequestProcessor[];
    containerTypes: ContainerType[];
    search: string;
}>();

const filterState = ref(<Record<string, string[]>>{});
const filterConfig = ref([
    { name: "name", prop: "name" },
    { name: "processor", prop: "requestProcessor" },
    { name: "samplesContainerType", prop: "samplesContainerType" },
    { name: "requestContainerType", prop: "requestContainerType" },
]);

const filterOptions = ref({});
const filteredRequestProtocols = computed(() => {
    return filterData(requestProtocolRows.value, filterState.value, filterConfig.value);
});

const requestProtocolRows = computed(() => {
    return requestProtocols.map(mapRequestProtocolToRow);
});

function mapRequestProtocolToRow(protocol: SavedRequestProtocol) {
    const requestProcessor =
        requestProcessors.find((rp) => rp.id === protocol.requestProcessorId)?.name || "N/A";
    const samplesContainerType =
        containerTypes.find((ct) => ct.id === protocol.samplesContainerTypeId)?.name || "N/A";
    const requestContainerType =
        containerTypes.find((ct) => ct.id === protocol.requestContainerTypeId)?.name || "N/A";

    return {
        ...protocol,
        requestProcessor,
        samplesContainerType,
        requestContainerType,
    };
}

watch(
    () => requestProtocols,
    (newRequestProtocols) => {
        filterOptions.value = buildFilterOptionsFromData(
            newRequestProtocols.map(mapRequestProtocolToRow),
            filterConfig.value
        );
    },
    { immediate: true }
);

const requestProtocolHeaders = ref([
    { title: "Protocol ID", value: "code", sortable: true },
    { title: "Protocol Name", value: "name", sortable: true },
    { title: "Processor", value: "requestProcessor", sortable: true },
    { title: "Source Samples Container Type", value: "samplesContainerType", sortable: false },
    { title: "Request Processing Container Type", value: "requestContainerType", sortable: false },
    { title: "Description", value: "url", sortable: false },
    { title: "Actions", value: "actions", sortable: false },
]);

function viewRequestProtocol(item: SavedRequestProtocol) {
    router.push({ path: `/requestsV2/config/protocol/${item.code}` });
}

function onUpdateFilters(newFilterState: Record<string, string[]>) {
    filterState.value = newFilterState;
}
</script>
